import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for scroll reveal animations with glossy effect
 * Similar to the Homey.co.uk effect where content reveals from under translucent glass
 */
export const useScrollReveal = (options = {}) => {
  const elementRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  const {
    threshold = 0.1,
    rootMargin = '0px',
    triggerOnce = true,
  } = options;

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [threshold, rootMargin, triggerOnce]);

  return [elementRef, isVisible];
};

/**
 * Wrapper component for scroll reveal with glossy effect
 */
export const ScrollReveal = ({ 
  children, 
  delay = 0, 
  duration = 800,
  className = '',
  threshold = 0.1,
  triggerOnce = true 
}) => {
  const [ref, isVisible] = useScrollReveal({ threshold, triggerOnce });

  return (
    <div
      ref={ref}
      className={`scroll-reveal ${isVisible ? 'is-visible' : ''} ${className}`}
      style={{
        '--reveal-delay': `${delay}ms`,
        '--reveal-duration': `${duration}ms`,
      }}
    >
      {children}
    </div>
  );
};

export default ScrollReveal;
